import { useState } from 'react';
import axios from 'axios';

export default function MatchDecisionPanel({
  menteeId,
  mentorId,
  role,
  milestone,
  onContinue,
  onRejectSuccess,
}) {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [decisionSaved, setDecisionSaved] = useState(false);

  // 1) Erfolgsansicht nach "Match fortsetzen"
  if (decisionSaved) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
        <div className="bg-white rounded-3xl shadow-2xl px-10 py-10 max-w-sm w-full text-center border border-[#53DD6C] relative">
          <svg
            className="w-16 h-16 text-[#53DD6C] mb-4 mx-auto"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <circle cx="12" cy="12" r="10" stroke="#8FFF8B" strokeWidth="2" fill="#E9FFE8" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 13l4 4 5-7" stroke="#53DD6C" strokeWidth="3" />
          </svg>
          <h3 className="text-2xl font-bold mb-2 text-gray-900">Entscheidung gespeichert</h3>
          <p className="text-gray-700 mb-6">
            Deine Entscheidung zum Match wurde gespeichert.
            <br />
            Du kannst dieses Browser-Fenster jetzt schließen.
          </p>
          <button
            className="px-6 py-3 bg-gradient-to-r from-[#8FFF8B] to-[#53DD6C] text-black rounded-xl font-bold shadow hover:from-[#53DD6C] hover:to-[#8FFF8B] transition text-lg"
            onClick={() => {
              // optional: Parent informieren (versucht zu schließen – wird evtl. vom Browser blockiert)
              if (onContinue) onContinue();
            }}
          >
            Fenster schließen
          </button>
        </div>
      </div>
    );
  }

  // 2) Standard-Ansicht "Wie möchtest du weitermachen?"
  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl shadow-2xl px-10 py-10 max-w-sm w-full text-center border border-[#53DD6C] relative">
        <svg
          className="w-16 h-16 text-[#53DD6C] mb-4 mx-auto"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <circle cx="12" cy="12" r="10" stroke="#8FFF8B" strokeWidth="2" fill="#E9FFE8" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 13l4 4 5-7" stroke="#53DD6C" strokeWidth="3" />
        </svg>
        <h3 className="text-2xl font-bold mb-2 text-gray-900">Wie möchtest du weitermachen?</h3>
        <div className="mb-3 text-gray-700 text-base">
          Du kannst das Mentoring ablehnen, falls die Chemie nicht stimmt.<br />
          Dein Gegenüber und HR werden informiert.<br />
          Optional kannst du den Grund angeben.
        </div>

        <div className="flex gap-5 justify-center mt-5 mb-2">
          {/* Match fortsetzen → nur lokalen Status umschalten */}
          <button
            className="px-6 py-3 bg-gradient-to-r from-[#8FFF8B] to-[#53DD6C] text-black rounded-xl font-bold shadow hover:from-[#53DD6C] hover:to-[#8FFF8B] transition text-lg"
            onClick={() => {
              setError('');
              setDecisionSaved(true);
              // KEIN window.close hier – wir zeigen zuerst den "Entscheidung gespeichert"-Screen
            }}
          >
            Match fortsetzen
          </button>

          <button
            className="px-6 py-3 bg-gradient-to-r from-[#F87171] to-[#FBBF24] text-white rounded-xl font-bold shadow hover:from-[#FBBF24] hover:to-[#F87171] transition text-lg"
            onClick={() => setShowRejectModal(true)}
          >
            Match ablehnen
          </button>
        </div>

        {/* Ablehnungsmodal */}
        {showRejectModal && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center">
            <div className="bg-white rounded-3xl shadow-2xl px-8 py-8 max-w-md w-full text-center border border-[#F87171] relative">
              <button
                className="absolute top-4 right-4 text-2xl text-gray-300 hover:text-gray-600 font-bold"
                onClick={() => setShowRejectModal(false)}
                title="Abbrechen"
                aria-label="Schließen"
              >
                ×
              </button>
              <h3 className="text-2xl font-bold mb-2 text-gray-900">
                Match ablehnen
              </h3>
              <p className="text-gray-700 mb-3">
                Du möchtest das Mentoring nach dem Kennenlerntermin beenden.<br />
                Optional kannst du einen Grund angeben (wird an HR/Admin weitergegeben):
              </p>
              <textarea
                className="w-full bg-gray-100 text-gray-900 border border-gray-300 rounded-xl p-3 mb-4 resize-none focus:ring-2 focus:ring-[#8FFF8B] focus:border-[#8FFF8B]"
                placeholder="Grund für die Ablehnung (optional)"
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                maxLength={300}
                rows={3}
              />
              {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
              {successMsg && (
                <div className="text-green-600 font-semibold mb-3">{successMsg}</div>
              )}
              <div className="flex gap-3 justify-center mt-4">
                <button
                  className="px-6 py-2 bg-gradient-to-r from-[#F87171] to-[#FBBF24] text-white rounded-xl font-bold shadow hover:from-[#FBBF24] hover:to-[#F87171] transition"
                  onClick={async () => {
                    setSubmitting(true);
                    setError('');
                    try {
                      await axios.post(
                        '/mentoring/reject-match',
                        {
                          mentee_id: menteeId,
                          mentor_id: mentorId,
                          role,
                          milestone,
                          reason: rejectReason.trim(),
                        },
                        { withCredentials: true }
                      );
                      setSuccessMsg('Das Match wurde erfolgreich beendet. Du erhältst neue Matchingoptionen, sobald verfügbar.');
                      setTimeout(() => {
                        setShowRejectModal(false);
                        setSuccessMsg('');
                        setRejectReason('');
                        if (onRejectSuccess) onRejectSuccess();
                      }, 1800);
                    } catch (e) {
                      setError('Fehler beim Ablehnen. Bitte versuche es erneut.');
                    }
                    setSubmitting(false);
                  }}
                  disabled={submitting}
                >
                  {submitting ? 'Wird gespeichert...' : 'Ablehnung bestätigen'}
                </button>
                <button
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-xl font-bold shadow hover:bg-gray-300 transition"
                  onClick={() => setShowRejectModal(false)}
                  disabled={submitting}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
